import clsx from 'clsx'
import React, { ComponentProps, createContext, CSSProperties, ReactNode, useContext, useState } from 'react'

import { NavLinkClickProperties } from '@Analytics/ampli'
import { Background } from '@Components/Background'
import { VimeoPlayer } from '@Components/CMS/Video/VimeoPlayer'
import { Container } from '@Components/Container'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import styles from '@Components/Tags/styles.module.scss'
import { ReferenceContentFragment } from '@Generated/graphql'

const context = createContext<({ backgroundColor, textColor }: { backgroundColor: string, textColor: string }) => void>(() => {})

export const Tags = ({ backgroundColor, children, textColor, ...props }: ComponentProps<typeof Container>) => {
  const [overrides, setOverrides] = useState({ backgroundColor: '', textColor: '' })

  return (
    <context.Provider value={setOverrides}>
      <Container
        backgroundColor={overrides.backgroundColor || backgroundColor}
        className={styles.center}
        textColor={overrides.textColor || textColor}
        {...props}
      >
        <div className={styles.tags}>
          {children}
        </div>
      </Container>
    </context.Provider>
  )
}

export const TagItem = ({
  analytics,
  background,
  backgroundColor,
  children,
  href,
  onClick,
  target,
  textColor,
  variant = 'spot',
}: {
  background?: ReferenceContentFragment
  backgroundColor?: string
  children?: ReactNode
  textColor?: string
  variant?: 'background' | 'dot' | 'spot'
} & (
  | {
    analytics?: NavLinkClickProperties
    href: string
    onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
    target?: string
  }
  | {
    analytics?: never
    href?: never
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
    target?: never
  }
)) => {
  const setOverrides = useContext(context)

  const onMouseLeave = () => {
    setOverrides({ backgroundColor: '', textColor: '' })
  }

  const onMouseOver = () => {
    setOverrides({ backgroundColor: backgroundColor || '', textColor: textColor || '' })
  }

  const style = {
    '--background-color': variant === 'background' ? backgroundColor : undefined,
    '--text-color': variant === 'background' ? textColor : undefined,
  } as CSSProperties

  const assetAsBackground = variant === 'background' || variant === 'dot'

  const content = (
    <>
      {assetAsBackground && <Background asset={background} className={styles.background} priority sizes="200px" />}
      {!assetAsBackground && (
        <>
          {
            (
              background?.__typename === 'ImageAsset'
              || background?.__typename === 'ImageResource'
              || background?.__typename === 'Link'
            )
            && background?.image?.url
            && (
              <ImageV4
                alt=""
                className={styles.image}
                draggable={false}
                src={background.image.url}
                width={100}
              />
            )
          }
          {background?.__typename === 'VideoResource' && background?.asset?.type === 'vimeo' && background?.asset?.key && (
            <VimeoPlayer
              className={styles.video}
              enableControls={false}
              options={{
                autoplay: true,
                muted: true,
                loop: true,
              }}
              videoID={background.asset.key}
            />
          )}
        </>
      )}

      {variant === 'dot' && (
        <div className={styles.dot} style={{ background: backgroundColor }} />
      )}

      <div className={styles.content}>
        {children}
      </div>
    </>
  )

  if (typeof href === 'string') {
    return (
      <Link
        analytics={analytics}
        className={clsx(
          styles.item,
          variant === 'background' && styles.variantBackground,
          variant === 'dot' && styles.variantDot,
          variant === 'spot' && styles.variantSpot,
        )}
        href={href}
        onClick={onClick}
        onMouseLeave={onMouseLeave}
        onMouseOver={onMouseOver}
        style={style}
        target={target}
      >
        {content}
      </Link>
    )
  }

  return (
    <div
      aria-current={onClick ? 'page' : undefined}
      className={clsx(
        styles.item,
        variant === 'background' && styles.variantBackground,
        variant === 'dot' && styles.variantDot,
        variant === 'spot' && styles.variantSpot,
      )}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      onMouseOver={onMouseOver}
      role={onClick ? 'link' : undefined}
      style={style}
    >
      {content}
    </div>
  )
}
